/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAuth, useVenueDetailsBySlug, useI18N } from "common/hooks";
import { useEffect, useState } from "react";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { defaultRegion } from "common/config";
import Loader from "@/components/common/Loader";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { Helmet } from "react-helmet";
import { Storage } from "common/storage";
import AnalyticsService from "@/services/analytics";
import WhatsNearbyContent from "../components/WhatsNearby/WhatsNearbyContent";
import { cn } from "@/lib/utils";

export type WhatsNearbyRouteParams = {
  venueId: string; // from the route path"/whats-nearby/$venueId"
};

export type WhatsNearbyRouteSearch = {
  source?: "map" | "activity";
  showAuthModal?: boolean;
  cid?: string;
  tid?: string;
  venue?: string;
};

export const Route = createFileRoute("/whats-nearby/$venueId")({
  component: WhatsNearby,
  validateSearch: (search: Record<string, unknown>): WhatsNearbyRouteSearch => {
    return {
      source: search.source as "map" | "activity",
      showAuthModal: search.showAuthModal as boolean,
      cid: search.cid as string,
      tid: search.tid as string,
      venue: search.venue as string | undefined,
    };
  },
});

function WhatsNearby() {
  const { venueId: venueSlug } = Route.useParams();
  const { showAuthModal, cid, tid, venue } = Route.useSearch();
  const navigate = useNavigate();
  const i18n = useI18N();

  const { user, isLoading, isFetching } = useAuth();
  const isAuthLoading = isLoading || isFetching;

  const [showAuthModalState, setShowAuthModalState] = useState(showAuthModal);
  const { location } = useGeoLocation();

  // Redirect to venue details page if venue query param is present on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const venueQuerySlug = urlParams.get("venue");
    if (venueQuerySlug) {
      // Redirect to the actual venue details page
      navigate({ to: `/venuedetails/${venueQuerySlug}` });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // load this venue from the server
  const {
    data,
    error,
    refetch: loadVenueDetails,
    isLoading: isDetailsLoading,
  } = useVenueDetailsBySlug({
    slug: venueSlug,
    latitude:
      location?.latitude.toString() || defaultRegion.latitude.toString(),
    longitude:
      location?.longitude.toString() || defaultRegion.longitude.toString(),
    accessToken: Storage.getCurrentUserToken(),
    enabled: location ? true : false,
    type: "Complex",
  });

  const { showModal } = useAuthModals();

  useEffect(() => {
    if (location && !isAuthLoading) {
      loadVenueDetails();
    }
  }, [location, loadVenueDetails, user, isAuthLoading]);

  useEffect(() => {
    if (showAuthModalState && !user && !isAuthLoading) {
      showModal(AuthModalScreen.EnterEmail);
      setShowAuthModalState(false);
    }
  }, [showAuthModalState, user, isAuthLoading, showModal]);

  useEffect(() => {
    if (data?.details) {
      // Track accessibility guide view with GA
      AnalyticsService.trackGAEventHandler("whatsNearbyViewed");

      // Track accessibility guide view with Mixpanel
      AnalyticsService.trackMx("whatsNearbyViewed", {
        VenueId: data.details.business.id,
        VenueName: data.details.name,
        VenueType: data.details.businessType,
        TemplateType: "Complex",
        Source: "whats_nearby",
        CampaignId: cid,
        TrackingId: tid,
      });
    }
  }, [data?.details, cid, tid]);

  return (
    <>      
      {(isAuthLoading || isDetailsLoading) && (
        <>
          <div role="status" aria-live="polite" className="sr-only">
            {i18n.get("loadingVenueDetails")}
          </div>
          <Loader />
        </>
      )}
      <main
        id="main"
        className="h-[calc(100vh-90px)] bg-bg-brand-secondary-default flex overflow-x-hidden max-w-[1440px] mx-auto"
      >

        {/* Regular Desktop View - Always mounted, hidden when showing accessibility or photos detail */}
        <div
          className={cn(
            "md:flex flex-1 min-w-0",
            // (showAccessibilityDetail || showPhotosDetail) && "!hidden",
          )}
        >
          {error && (
            <div className="max-w-[1160px] px-4 mx-auto overflow-hidden">
              <div role="alert">{error.message}</div>
            </div>
          )}

          {data && (
            <WhatsNearbyContent
              venueDetailsResponse={data}
              venueSlug={venueSlug}
              venueParam={venue}
              onClose={() => {
                // Always navigate back to the main venue details page using the actual venue slug
                const actualVenueSlug = data.details.business.slug;
                window.location.href = `/venuedetails/${actualVenueSlug}`;
              }}
              onRefresh={() => {
                setTimeout(() => loadVenueDetails(), 1000);
              }}
              cid={cid}
              tid={tid}
            />
          )}
        </div>
      </main>
    </>
  );
}
